import type { MotionElement, ResolvedMotionCaption } from "./types";
import { motionElementLocalTime } from './evaluateMotionElement';
export function splitMotionText(element: MotionElement): MotionElement[] {
  const text = element.content ?? "";
  const parts =
    element.split === "line"
      ? text.split("\n")
      : element.split === "word"
        ? text.trim().split(/\s+/).filter(Boolean)
        : element.split === "char"
          ? Array.from(text)
          : [text];
  return parts.map((content, i) => ({
    ...element,
    id: `${element.id}-part-${i}`,
    content,
    split: "none",
    start: element.start + i * (element.stagger ?? element.enter?.stagger ?? 0),
    repeat: undefined,
  }));
}
/** A pause this long between two words starts a new caption cue. */
export const MOTION_CAPTION_PAUSE_SECONDS = 0.7;

/** Cue boundaries ([first, last) word indexes): at most `limit` words, and a
 * new cue after a pause. The caption tool assigns tiers with the same cues
 * the renderer shows. */
export function motionCaptionCues(
  words: ReadonlyArray<{ start: number; end: number }>,
  limit = 6,
): Array<{ first: number; last: number }> {
  const cues: Array<{ first: number; last: number }> = [];
  let first = 0;
  while (first < words.length) {
    let last = first + 1;
    while (
      last < words.length &&
      last - first < limit &&
      words[last].start - words[last - 1].end < MOTION_CAPTION_PAUSE_SECONDS
    )
      last++;
    cues.push({ first, last });
    first = last;
  }
  return cues;
}

/** Words are resolved once from transcript by the editor; no network or transcript lookup during render. */
export function resolveMotionCaptions(
  element: MotionElement,
  time: number,
): ResolvedMotionCaption[] {
  if (
    element.type !== "captions" ||
    time < element.start ||
    time >= element.end
  )
    return [];
  const local = motionElementLocalTime(element, time);
  const words = element.words ?? [];
  for (const { first, last } of motionCaptionCues(words, element.wordsPerCue ?? 6)) {
    const cue = words.slice(first, last);
    const cueStart = cue[0].start;
    const cueEnd = Math.max(...cue.map((w) => w.end));
    if (local >= cueStart && local < cueEnd) {
      return cue.map((word, i) => {
        const tiers = element.tiers ?? [];
        const tier = tiers.find(
          (t) => t.id === (word.tier ?? element.defaultTier),
        ) ??
          tiers[0] ?? {
            id: "default",
            font: element.font,
            size: element.size ?? 64,
            fill: element.fill ?? "#ffffff",
          };
        // A tier's own word animation wins over the element's.
        const wordEnter = tier.wordEnter ?? element.wordEnter;
        const activeWord = tier.activeWord ?? element.activeWord;
        const spokenWord = tier.spokenWord ?? element.spokenWord;
        return {
          ...word,
          index: first + i,
          active: local >= word.start && local < word.end,
          style: { ...tier },
          cueStart,
          cueEnd,
          ...(wordEnter ? { wordEnter } : {}),
          ...(activeWord ? { activeWord } : {}),
          ...(spokenWord ? { spokenWord } : {}),
        };
      });
    }
  }
  return [];
}
